import React from "react";
import { FileText, FileCode2, Sheet, Image as ImageIcon } from "lucide-react";
import ChapterShell from "@/tutorial/ChapterShell";
import {
  Section,
  Step,
  AnnotatedScreen,
  InfoBox,
} from "@/tutorial/components/TutorialComponents";

const FORMATS = [
  {
    icon: FileText,
    name: "PDF",
    duration: "≈ 15 s",
    purpose: "Rapport complet imprimable, archivable, immuable.",
    contains: "Hero + KPIs + 6 graphiques + analyse juridique + dispositif vitaliste.",
    color: "text-red-600 dark:text-red-400",
    border: "border-red-500/40",
    bg: "bg-red-500/5",
  },
  {
    icon: FileCode2,
    name: "Word (.docx)",
    duration: "≈ 8 s",
    purpose: "Rapport éditable pour intégration dans un dossier ou tribune.",
    contains: "Texte structuré, tableaux, liens internes, captures incorporées.",
    color: "text-brand-blue",
    border: "border-brand-blue/40",
    bg: "bg-brand-blue/5",
  },
  {
    icon: Sheet,
    name: "Excel (.xlsx)",
    duration: "≈ 5 s",
    purpose: "Données brutes + formules pour traitement statistique.",
    contains: "4 onglets : budget, calculs vitalistes, juridique, données brutes.",
    color: "text-emerald-600 dark:text-emerald-400",
    border: "border-emerald-500/40",
    bg: "bg-emerald-500/5",
  },
  {
    icon: ImageIcon,
    name: "Poster (.png)",
    duration: "≈ 10 s",
    purpose: "Infographie A3 haute résolution pour conférences et campagnes.",
    contains: "Synthèse visuelle 1 page : KPIs + donut + jour de libération.",
    color: "text-brand-gold",
    border: "border-brand-gold/50",
    bg: "bg-brand-gold/10",
  },
];

export default function Chapter8() {
  return (
    <ChapterShell id={8}>
      <Section id="section-8-1" eyebrow="8.1" title="Choisir le bon format">
        <p>
          Quatre formats couvrent tous les usages, de l'impression
          institutionnelle à la diffusion grand public.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {FORMATS.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.name}
                data-testid={`chapter-8-format-${f.name.split(" ")[0].toLowerCase()}`}
                className={`rounded-xl border ${f.border} ${f.bg} p-4`}
              >
                <div className="flex items-start gap-3">
                  <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-card ${f.color}`}>
                    <Icon className="h-6 w-6" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-lg font-bold">{f.name}</div>
                      <span className="text-[11px] font-mono text-muted-foreground">
                        {f.duration}
                      </span>
                    </div>
                    <p className="mt-1 text-sm font-semibold text-foreground/85">
                      {f.purpose}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {f.contains}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <Section id="section-8-2" eyebrow="8.2" title="Lancer la génération">
        <Step number={1} title="Aller à l'étape 5/5 du wizard">
          Étape « Rapport » dans l'assistant en sept étapes.
        </Step>
        <Step number={2} title="Sélectionner le format souhaité">
          PDF, Word, Excel ou Poster — vous pouvez en générer plusieurs à la suite.
        </Step>
        <Step number={3} title="Cliquer sur « Générer le rapport »">
          La barre de progression apparaît dans la zone de droite.
        </Step>
        <Step number={4} title="Patienter pendant la génération">
          La génération est entièrement client (jsPDF / docx / xlsx / html2canvas).
          Aucun appel serveur, vos données restent locales.
        </Step>
        <Step number={5} title="Cliquer sur « Télécharger »">
          Le fichier est nommé automatiquement (voir section suivante).
        </Step>
      </Section>

      <Section
        id="section-8-3"
        eyebrow="8.3"
        title="Nommage automatique des fichiers"
      >
        <InfoBox title="Convention de nommage">
          Vos fichiers sont nommés automatiquement :
          <div className="mt-2 rounded bg-card px-3 py-2 font-mono text-xs">
            VITAE-PUBLICA_[Pays]_[AnnéeFiscale]_[Date].pdf
          </div>
          <div className="mt-1 text-xs italic">
            Exemple : <code>VITAE-PUBLICA_Mauritanie_2025_20260424.pdf</code>
          </div>
        </InfoBox>
        <p>
          Cette convention garantit l'archivage chronologique et la traçabilité
          institutionnelle. Vous pouvez la modifier dans <em>Paramètres → Exports</em>.
        </p>
      </Section>

      <Section
        id="section-8-4"
        eyebrow="8.4"
        title="Retrouver et partager vos rapports"
      >
        <AnnotatedScreen
          src="/tutorial/images/chapter8-reports.png"
          alt="Liste des rapports générés"
          annotations={[
            { x: 20, y: 35, label: "①", text: "Liste chronologique des rapports — re-téléchargement instantané." },
            { x: 60, y: 50, label: "②", text: "Bouton « Partager » : génère un lien public sécurisé." },
            { x: 88, y: 50, label: "③", text: "Lien expirant à 7 jours par défaut, révocable à tout moment." },
          ]}
        />
        <InfoBox title="Partage sécurisé">
          Le bouton <em>Partager</em> génère un lien d'accès public
          (cryptographique) à l'analyse, valable <strong>7 jours par défaut</strong>.
          Le lien peut être révoqué à tout moment depuis « Mes analyses ».
        </InfoBox>
      </Section>
    </ChapterShell>
  );
}
